import { useState } from 'react';
import { C, FONTS } from '../../tokens/design';
import Shell from '../../components/layout/Shell';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Tag from '../../components/ui/Tag';
import Skeleton from '../../components/ui/Skeleton';
import PageError from '../../components/ui/PageError';
import { TH, TR, TD } from '../../components/ui/Table';
import { useMspTeam } from '../../hooks/useMspData';

const ROLES = [
  { name: 'MSP Admin', desc: 'Full access to all features, settings, and team management', color: C.red },
  { name: 'Security Analyst', desc: 'View/manage findings, run scans, generate reports', color: C.sky },
  { name: 'Account Manager', desc: 'View dashboards, reports, and client management', color: C.pur },
  { name: 'Read-Only', desc: 'View-only access to dashboards and reports', color: C.gry },
];

const ROLE_NAMES = { 0: 'MSP Admin', 1: 'Security Analyst', 2: 'Account Manager', 3: 'Read-Only' };

function getInitials(name) {
  return name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
}

export default function TeamMembers() {
  const { data: members, loading, error, refetch } = useMspTeam();
  const [q, setQ] = useState('');
  const filtered = members.filter(m =>
    (m.displayName || '').toLowerCase().includes(q.toLowerCase()) ||
    (m.email || '').toLowerCase().includes(q.toLowerCase())
  );
  const activeCount = members.filter(m => m.isActive).length;

  if (loading) {
    return (
      <Shell title="Team Members" sub="Manage users & roles">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 14 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Skeleton height={36} />
            <Card style={{ padding: 0 }}>
              <div style={{ padding: 14 }}><Skeleton width="40%" height={12} style={{ marginBottom: 10 }} /></div>
              {[0,1,2,3].map(i => (
                <div key={i} style={{ display: 'flex', gap: 12, padding: '10px 14px', borderTop: `1px solid ${C.b2}` }}>
                  <Skeleton width={30} height={30} style={{ borderRadius: '50%' }} />
                  <div style={{ flex: 1 }}><Skeleton width="50%" height={12} style={{ marginBottom: 6 }} /><Skeleton width="70%" height={10} /></div>
                  <Skeleton width={60} height={18} />
                </div>
              ))}
            </Card>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Card style={{ padding: 18 }}><Skeleton width="50%" height={12} style={{ marginBottom: 12 }} />{[0,1,2,3].map(i => <Skeleton key={i} height={32} style={{ marginBottom: 8 }} />)}</Card>
            <Card style={{ padding: 18 }}><Skeleton width="50%" height={12} style={{ marginBottom: 12 }} />{[0,1,2].map(i => <Skeleton key={i} height={18} style={{ marginBottom: 6 }} />)}</Card>
          </div>
        </div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell title="Team Members" sub="Manage users & roles">
        <PageError error={error} onRetry={refetch} />
      </Shell>
    );
  }

  return (
    <Shell title="Team Members" sub={`${members.length} users · ${activeCount} active`}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search team members..." style={{ flex: 1, background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6, padding: '8px 11px', color: C.t1, fontSize: 12, fontFamily: FONTS.mono, outline: 'none' }} />
            <Button variant="pri" disabled>+ Add Member</Button>
          </div>

          {members.length === 0 ? (
            <Card style={{ padding: 40, textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: C.t2, fontFamily: FONTS.mono }}>No team members found.</div>
            </Card>
          ) : (
            <Card>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <TH cols={['Member', 'Role', 'Email', 'Status', '']} />
                <tbody>
                  {filtered.map(m => {
                    const roleName = ROLE_NAMES[m.role] || `Role ${m.role}`;
                    const initials = getInitials(m.displayName || m.email || '??');
                    return (
                      <TR key={m.memberId}>
                        <TD>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(135deg,#334155,#1e293b)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff', flexShrink: 0 }}>{initials}</div>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>{m.displayName}</div>
                              {m.title && <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>{m.title}</div>}
                            </div>
                          </div>
                        </TD>
                        <TD><Tag label={roleName} color={(Array.isArray(ROLES) ? ROLES : []).find(r => r.name === roleName)?.color || C.t2} /></TD>
                        <TD style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{m.email}</TD>
                        <TD><Tag label={m.isActive ? 'active' : 'inactive'} color={m.isActive ? C.grn : C.red} /></TD>
                        <TD>
                          <div style={{ display: 'flex', gap: 5 }}>
                            <Button variant="sec" style={{ fontSize: 10 }} disabled>Edit</Button>
                          </div>
                        </TD>
                      </TR>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Card style={{ padding: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 12, color: C.t1 }}>Roles</div>
            {ROLES.map(r => (
              <div key={r.name} style={{ padding: '10px 0', borderBottom: `1px solid ${C.b2}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: r.color }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: C.t1, fontFamily: FONTS.sans }}>{r.name}</span>
                </div>
                <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, paddingLeft: 14 }}>{r.desc}</div>
              </div>
            ))}
          </Card>

          <Card style={{ padding: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 12, color: C.t1 }}>Team Stats</div>
            {[['Total Members', String(members.length)], ['Active', String(activeCount)], ['Inactive', String(members.length - activeCount)]].map(([l, v]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${C.b2}` }}>
                <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{l}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.t1, fontFamily: FONTS.mono }}>{v}</span>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </Shell>
  );
}
